// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

import { useState } from 'react'
import * as api from '../api/client'
import SearchableSelect from './common/SearchableSelect'

function parseMulti(v: string | null | undefined): string[] {
  if (!v) return []
  try { const a = JSON.parse(v); return Array.isArray(a) ? a.map(String) : (v ? [v] : []) }
  catch { return v ? [v] : [] }
}

/**
 * Editable value for a custom view-builder field, typed by data_type.
 * Editable when: canModify, or (no existing value AND canWrite). Otherwise read-only.
 * Saves through the tiered, audited /custom-field-values endpoint.
 */
export default function CustomFieldInput({ surface, recordId, cardKey, field, initialValue, canWrite, canModify }: {
  surface: string; recordId: string; cardKey: string; field: any
  initialValue: string | null | undefined; canWrite: boolean; canModify: boolean
}) {
  const dataType: string = field.data_type || 'text'
  const fieldColumn: string = field.owlrx_column
  // ASP-43: per-field options[] + default live in the card's config_json field meta.
  const options: string[] = Array.isArray(field.options) ? field.options.map(String).filter(Boolean) : []
  const defaultValue: string = typeof field.default_value === 'string' ? field.default_value : ''
  // A field that has a PioneerRx mapping (pioneer_path) is system-populated from the ingest feed —
  // it is NOT a manual-entry field. An empty mapped field must read as "—", never as an editable
  // "Type a value…" box (which would invite manual entry into a field the system owns).
  const isMapped: boolean = !!(field.pioneer_path && String(field.pioneer_path).length > 0)
  const [hasValue, setHasValue] = useState(initialValue != null && initialValue !== '')
  const editable = !isMapped && (canModify || (!hasValue && canWrite))
  const [val, setVal] = useState<string>(initialValue ?? '')
  const [list, setList] = useState<string[]>(() => parseMulti(initialValue))
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle')
  const [errMsg, setErrMsg] = useState<string | null>(null)

  async function save(value: string | null) {
    setStatus('saving'); setErrMsg(null)
    try {
      await api.putCustomFieldValue({ surface, record_id: recordId, card_key: cardKey, field_column: fieldColumn, value })
      setHasValue(value != null && value !== '')
      setStatus('saved'); setTimeout(() => setStatus(s => (s === 'saved' ? 'idle' : s)), 1500)
    } catch (e: any) {
      setStatus('error')
      setErrMsg(e?.payload?.error === 'forbidden' ? 'You can’t change this value.' : 'Save failed.')
    }
  }

  const statusEl = status === 'saving' ? <span style={hint}>saving…</span>
    : status === 'saved' ? <span style={{ ...hint, color: '#1E7A4D' }}>saved ✓</span>
    : status === 'error' ? <span style={{ ...hint, color: '#B23A22' }}>{errMsg}</span>
    : null

  if (!editable) {
    const display = dataType === 'multi_text' ? parseMulti(initialValue).join(', ') : (initialValue ?? '')
    // ASP-44/45/46: an EMPTY value displays the configured default (display-level only — nothing
    // is written until a user actually picks a value; 'allow manual changes' preserved).
    return <span>{display || (defaultValue ? <span title="Default (not yet set)">{defaultValue}</span> : '—')}</span>
  }

  // ASP-43: options configured → dropdown. Empty selection shows the default as the placeholder
  // state; choosing any option (incl. re-choosing the default) writes normally through the
  // audited endpoint. 'Clear' returns to unset (default display).
  if (options.length > 0 && dataType !== 'multi_text') {
    const selOpts = [
      { value: '', label: defaultValue ? `${defaultValue} (default)` : '— select —' },
      ...options.map(o => ({ value: o, label: o })),
      ...(val && !options.includes(val) ? [{ value: val, label: val }] : []),
    ]
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        <SearchableSelect value={val} options={selOpts} ariaLabel={field.owlrx_label || fieldColumn}
          placeholder={defaultValue ? `${defaultValue} (default)` : '— select —'}
          onChange={v => { setVal(v); save(v || null) }} style={{ minWidth: 170 }} />
        {statusEl}
      </span>
    )
  }

  if (dataType === 'multi_text') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        {list.map((item, i) => (
          <div key={i} style={{ display: 'flex', gap: 6 }}>
            <input value={item} onChange={e => setList(l => l.map((x, j) => j === i ? e.target.value : x))}
              onBlur={() => save(JSON.stringify(list))} placeholder="Type a value…" style={input} />
            <button onClick={() => { const nl = list.filter((_, j) => j !== i); setList(nl); save(JSON.stringify(nl)) }}
              style={rm} aria-label="Remove">×</button>
          </div>
        ))}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button onClick={() => setList(l => [...l, ''])} style={addBtn}>+ add</button>
          {statusEl}
        </div>
      </div>
    )
  }

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <input type={dataType === 'date' ? 'date' : 'text'} value={val}
        onChange={e => setVal(e.target.value)}
        onBlur={() => { if (val !== (initialValue ?? '')) save(val || null) }}
        placeholder="Type a value…" style={input} />
      {statusEl}
    </span>
  )
}

const input: React.CSSProperties = { padding: '5px 8px', border: '1px solid #DED8CC', borderRadius: 7, background: 'var(--color-surface,#fff)', fontSize: 13, color: 'var(--color-text,#1C1B19)', minWidth: 160, fontFamily: 'inherit' }
const rm: React.CSSProperties = { border: '1px solid #DED8CC', background: '#fff', borderRadius: 7, width: 28, cursor: 'pointer', color: '#B23A22', fontSize: 15 }
const addBtn: React.CSSProperties = { border: '1px dashed #C9963F', background: 'transparent', color: '#946317', borderRadius: 7, padding: '3px 9px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }
const hint: React.CSSProperties = { fontSize: 11, color: 'var(--color-text-mute,#7C766B)' }
